import { useCallback, type ChangeEvent } from 'react';
import { FormControl, TextField } from '@material-ui/core';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import {
  configApiRef,
  fetchApiRef,
  type ApiHolder,
} from '@backstage/core-plugin-api';

const EXISTS_MESSAGE =
  'A QTest project with this name already exists. Please enter a different project name.';
const CHECK_FAILED_MESSAGE =
  'Could not verify whether this project name already exists. Try again.';

export async function validateQtestProjectName(
  value: string | undefined,
  validation: FieldValidation,
  context: { apiHolder: ApiHolder },
): Promise<void> {
  const name = typeof value === 'string' ? value.trim() : '';
  if (!name) {
    return;
  }

  const configApi = context.apiHolder.get(configApiRef);
  const fetchApi = context.apiHolder.get(fetchApiRef);
  if (!configApi || !fetchApi) {
    validation.addError(CHECK_FAILED_MESSAGE);
    return;
  }

  const backendBaseUrl = configApi.getString('backend.baseUrl');
  const params = new URLSearchParams({ name });
  const url = `${backendBaseUrl}/api/qtest-project-name/exists?${params}`;

  try {
    const response = await fetchApi.fetch(url);
    const data = (await response.json().catch(() => ({}))) as {
      exists?: boolean;
      error?: string;
    };
    if (!response.ok) {
      validation.addError(data.error || CHECK_FAILED_MESSAGE);
      return;
    }
    if (data.exists) {
      validation.addError(EXISTS_MESSAGE);
    }
  } catch {
    validation.addError(CHECK_FAILED_MESSAGE);
  }
}

export const QtestProjectNameField = ({
  onChange,
  rawErrors,
  formData,
  required,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  const label = schema?.title ?? '';
  const description = schema?.description ?? '';
  const placeholder = (uiSchema as { 'ui:placeholder'?: string } | undefined)?.[
    'ui:placeholder'
  ];
  const maxLength =
    typeof schema?.maxLength === 'number' ? schema.maxLength : undefined;
  const error = Boolean(rawErrors?.length);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  return (
    <FormControl margin="normal" required={required} error={error} fullWidth>
      <TextField
        label={label || undefined}
        placeholder={placeholder}
        value={formData ?? ''}
        onChange={handleChange}
        variant="outlined"
        fullWidth
        error={error}
        helperText={error ? undefined : description || undefined}
        inputProps={{ maxLength }}
      />
    </FormControl>
  );
};
